/**
 * Effort durations — DESIGN.md §6.13, D14.
 *
 * Time is entered as `2h 30m` shorthand and stored as whole minutes. Days are
 * deliberately unsupported: "1d" invites an 8h-vs-24h argument no internal
 * tool should have.
 */
/** Thrown by `parseDuration` on unparseable input; carries a UI-ready message. */
export class DurationParseError extends Error {
    input;
    constructor(input) {
        super(`Could not read "${input}" as a duration. Try 90m, 2h, or 1h 30m.`);
        this.input = input;
        this.name = 'DurationParseError';
    }
}
const DURATION_RE = /^\s*(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+)\s*m(?:in)?)?\s*$/i;
const BARE_NUMBER_RE = /^\s*(\d+(?:\.\d+)?)\s*$/;
/**
 * Parse `1h 30m`, `90m`, `2h`, `1.5h`, or a bare number (read as minutes)
 * into whole minutes. Returns `null` for empty input — an empty estimate field
 * is a legitimate "not estimated", not an error.
 */
export function parseDuration(input) {
    const trimmed = input.trim();
    if (trimmed === '')
        return null;
    const bare = BARE_NUMBER_RE.exec(trimmed);
    if (bare?.[1]) {
        const minutes = Math.round(Number(bare[1]));
        if (minutes < 0)
            throw new DurationParseError(input);
        return minutes;
    }
    const match = DURATION_RE.exec(trimmed);
    if (!match || (match[1] === undefined && match[2] === undefined)) {
        throw new DurationParseError(input);
    }
    const hours = match[1] ? Number(match[1]) : 0;
    const mins = match[2] ? Number(match[2]) : 0;
    const total = Math.round(hours * 60 + mins);
    if (!Number.isFinite(total) || total < 0)
        throw new DurationParseError(input);
    return total;
}
/**
 * Format whole minutes as `2h 30m`. Zero renders as `0m` so a logged-time
 * column never shows a bare dash where a real zero was meant.
 */
export function formatDuration(minutes) {
    if (minutes === null || minutes === undefined)
        return '—';
    const sign = minutes < 0 ? '-' : '';
    const abs = Math.abs(Math.round(minutes));
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    if (h === 0)
        return `${sign}${m}m`;
    if (m === 0)
        return `${sign}${h}h`;
    return `${sign}${h}h ${m}m`;
}
/** Compact form for dense UI (list columns, chips): `2:30`. */
export function formatDurationCompact(minutes) {
    if (minutes === null || minutes === undefined)
        return '—';
    const abs = Math.abs(Math.round(minutes));
    return `${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`;
}
/**
 * Elapsed minutes of a running timer, rounded to the nearest minute with a
 * floor of 1 — §6.13: "Stop creates an entry (rounded to the nearest minute,
 * minimum 1)". A 20-second timer logs a minute rather than vanishing.
 */
export function timerElapsedMinutes(startedAt, now = new Date()) {
    const ms = now.getTime() - startedAt.getTime();
    return Math.max(1, Math.round(ms / 60_000));
}
//# sourceMappingURL=duration.js.map